import React, { lazy, Suspense, useContext, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import Loading from './components/Loading';
import User from './pages/User';
import { PrivateRoute } from './components/PrivateRoute';
import RegisterForm from './components/RegisterForm';
import LoginForm from './components/LoginForm';
import NotFound from './pages/NotFound';
import Setting from './pages/Setting';
import EditUser from './pages/User/EditUser';
import AddUser from './pages/User/AddUser';
import { API, getLogo, getSystemName, showError, showNotice } from './helpers';
import PasswordResetForm from './components/PasswordResetForm';
import GitHubOAuth from './components/GitHubOAuth';
import PasswordResetConfirm from './components/PasswordResetConfirm';
import { UserContext } from './context/User';
import { StatusContext } from './context/Status';
import Channel from './pages/Channel';
import Token from './pages/Token';
import EditToken from './pages/Token/EditToken';
import EditChannel from './pages/Channel/EditChannel';
import Redemption from './pages/Redemption';
import EditRedemption from './pages/Redemption/EditRedemption';
import TopUp from './pages/TopUp';
import Subscriptions from './pages/Subscriptions';
import Log from './pages/Log';
import Chat from './pages/Chat';
import LarkOAuth from './components/LarkOAuth';
import Dashboard from './pages/Dashboard';
import Playground from './pages/Playground';
import Access from './pages/Access';
import Operator from './pages/Operator';
import ModelManagement from './pages/Model';
import UserConsoleLayout from './components/UserConsoleLayout';

const About = lazy(() => import('./pages/About'));

function App() {
  const [userState, userDispatch] = useContext(UserContext);
  const [statusState, statusDispatch] = useContext(StatusContext);

  const loadUser = () => {
    let user = localStorage.getItem('user');
    if (user) {
      let data = JSON.parse(user);
      userDispatch({ type: 'login', payload: data });
    }
  };
  const loadStatus = async () => {
    try {
      const res = await API.get('/api/status');
      const { success, message, data } = res.data || {}; // Add default empty object
      if (success && data) {
        // Check data exists
        localStorage.setItem('status', JSON.stringify(data));
        statusDispatch({ type: 'set', payload: data });
        localStorage.setItem('system_name', data.system_name);
        localStorage.setItem('logo', data.logo);
        localStorage.setItem('footer_html', data.footer_html);
        localStorage.setItem('quota_per_unit', data.quota_per_unit);
        localStorage.setItem(
          'seedance_rmb_per_million_tokens',
          data.seedance_rmb_per_million_tokens || 92
        );
        localStorage.setItem('display_in_currency', data.display_in_currency);
        if (data.chat_link) {
          localStorage.setItem('chat_link', data.chat_link);
        } else {
          localStorage.removeItem('chat_link');
        }
        if (
          data.version !== process.env.REACT_APP_VERSION &&
          data.version !== 'v0.0.0' &&
          process.env.REACT_APP_VERSION !== ''
        ) {
          showNotice(
            `新版本可用：${data.version}，请使用快捷键 Shift + F5 刷新页面`
          );
        }
      } else {
        showError(message || '无法正常连接至服务器！');
      }
    } catch (error) {
      showError(error.message || '无法正常连接至服务器！');
    }
  };

  useEffect(() => {
    loadUser();
    loadStatus().then();
    let systemName = getSystemName();
    if (systemName) {
      document.title = systemName;
    }
    let logo = getLogo();
    if (logo) {
      let linkElement = document.querySelector("link[rel~='icon']");
      if (linkElement) {
        linkElement.href = logo;
      }
    }
  }, []);

  const renderMaybeConsole = (children) => {
    if (userState.user || localStorage.getItem('user')) {
      return <UserConsoleLayout>{children}</UserConsoleLayout>;
    }
    return children;
  };

  return (
    <Routes>
      <Route
        path='/'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <LoginForm />
          </Suspense>
        }
      />
      <Route
        element={
          <PrivateRoute>
            <UserConsoleLayout />
          </PrivateRoute>
        }
      >
        <Route path='/channel' element={<Channel />} />
        <Route
          path='/channel/edit/:id'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <EditChannel />
            </Suspense>
          }
        />
        <Route
          path='/channel/add'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <EditChannel />
            </Suspense>
          }
        />
        <Route path='/token' element={<Token />} />
        <Route
          path='/token/edit/:id'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <EditToken />
            </Suspense>
          }
        />
        <Route
          path='/token/add'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <EditToken />
            </Suspense>
          }
        />
        <Route path='/redemption' element={<Redemption />} />
        <Route
          path='/redemption/edit/:id'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <EditRedemption />
            </Suspense>
          }
        />
        <Route
          path='/redemption/add'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <EditRedemption />
            </Suspense>
          }
        />
        <Route path='/user' element={<User />} />
        <Route
          path='/user/edit/:id'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <EditUser />
            </Suspense>
          }
        />
        <Route
          path='/user/edit'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <EditUser />
            </Suspense>
          }
        />
        <Route
          path='/user/add'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <AddUser />
            </Suspense>
          }
        />
        <Route
          path='/setting'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <Setting />
            </Suspense>
          }
        />
        <Route
          path='/topup'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <TopUp />
            </Suspense>
          }
        />
        <Route
          path='/subscriptions'
          element={
            <Suspense fallback={<Loading></Loading>}>
              <Subscriptions />
            </Suspense>
          }
        />
        <Route path='/log' element={<Log />} />
        <Route path='/dashboard' element={<Dashboard />} />
        <Route path='/dashboard/playground' element={<Playground />} />
        <Route path='/access' element={<Access />} />
        <Route path='/operator' element={<Operator />} />
        <Route path='/model' element={<ModelManagement />} />
      </Route>
      <Route
        path='/user/reset'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <PasswordResetConfirm />
          </Suspense>
        }
      />
      <Route
        path='/login'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <LoginForm />
          </Suspense>
        }
      />
      <Route
        path='/register'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <RegisterForm />
          </Suspense>
        }
      />
      <Route
        path='/reset'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <PasswordResetForm />
          </Suspense>
        }
      />
      <Route
        path='/oauth/github'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <GitHubOAuth />
          </Suspense>
        }
      />
      <Route
        path='/oauth/lark'
        element={
          <Suspense fallback={<Loading></Loading>}>
            <LarkOAuth />
          </Suspense>
        }
      />
      <Route
        path='/about'
        element={
          <Suspense fallback={<Loading></Loading>}>
            {renderMaybeConsole(<About />)}
          </Suspense>
        }
      />
      <Route
        path='/chat'
        element={
          <Suspense fallback={<Loading></Loading>}>
            {renderMaybeConsole(<Chat />)}
          </Suspense>
        }
      />
      <Route path='*' element={<NotFound />} />
    </Routes>
  );
}

export default App;
